import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { INVOICE_STATUS, dateLabel } from '@adisys/shared';
import { api } from '../../lib/api';
import { inr2, qtyLabel } from '../../lib/crm';
import { Button, ErrorState, Skeleton } from '../../components/ui';

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
  'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const below100 = (n: number) => (n < 20 ? ONES[n]! : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const below1000 = (n: number) => (n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${below100(n % 100)}` : ''}` : below100(n));

/** Rupee amount in Indian words (crore / lakh / thousand). */
export function rupeesInWords(amount: number): string {
  const rupees = Math.floor(Math.round(amount * 100) / 100);
  const paise = Math.round((amount - rupees) * 100);
  if (rupees === 0 && paise === 0) return 'Rupees Zero Only';
  const parts: string[] = [];
  let n = rupees;
  for (const [div, label] of [[10_000_000, 'Crore'], [100_000, 'Lakh'], [1000, 'Thousand']] as const) {
    const q = Math.floor(n / div);
    if (q) parts.push(`${below1000(q)} ${label}`);
    n %= div;
  }
  if (n) parts.push(below1000(n));
  const r = parts.length ? `Rupees ${parts.join(' ')}` : 'Rupees Zero';
  return `${r}${paise ? ` and ${below100(paise)} Paise` : ''} Only`;
}

export function InvoiceDocumentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['crm', 'invoice', id], queryFn: () => api.get(`/crm/invoices/${id}`) });
  if (q.isLoading) return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const i = q.data;
  const co = i.company ?? {};
  // Same rule as the server: with no state recorded, supply is treated as within the seller's state.
  const intra = !i.billTo?.state || String(i.billTo.state).trim().toLowerCase() === String(co.state ?? '').trim().toLowerCase();
  const gst = Number(i.gstTotal);

  return (
    <div className="min-h-dvh bg-ink-100 py-6 print:bg-white print:py-0">
      <style>{'@page { size: A4; margin: 12mm; } @media print { body { background: #fff; } }'}</style>
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-between px-2">
        <Button icon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate(-1)}>Back</Button>
        <Button variant="primary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>
      <article className="mx-auto max-w-[210mm] bg-white p-8 text-[13px] leading-relaxed text-ink-900 shadow-card print:max-w-none print:p-0 print:shadow-none">
        <header className="flex items-start justify-between gap-6 border-b-2 border-ink-900 pb-4">
          <div>
            <h1 className="text-xl font-bold">{co.name}</h1>
            {co.address && <p className="mt-1 max-w-sm whitespace-pre-line text-ink-600">{co.address}</p>}
            {co.gstin && <p className="mt-1 font-medium">GSTIN: {co.gstin}</p>}
            {co.email && <p className="text-ink-600">{co.email}</p>}
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold tracking-wide">TAX INVOICE</h2>
            <p className="mt-1 font-semibold">{i.invoiceNo}</p>
            <p>Date: {dateLabel(i.invoiceDate)}</p>
            <p>Due: {dateLabel(i.dueDate)}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 border-b border-line py-4">
          <div>
            <p className="text-xs font-semibold uppercase text-ink-500">Bill to</p>
            <p className="font-semibold">{i.billTo?.company}</p>
            {i.billTo?.address && <p className="text-ink-700">{i.billTo.address}</p>}
            {i.billTo?.gstin && <p>GSTIN: {i.billTo.gstin}</p>}
            {i.billTo?.contact && <p className="text-ink-600">Attn: {i.billTo.contact}{i.billTo.mobile ? ` · ${i.billTo.mobile}` : ''}</p>}
          </div>
          <div className="text-right">
            <p><span className="text-ink-500">Place of supply:</span> {i.placeOfSupply}</p>
            <p><span className="text-ink-500">Order:</span> {i.orderNo}</p>
            <p><span className="text-ink-500">Project:</span> {i.projectCode}</p>
          </div>
        </section>

        <table className="mt-4 w-full border-collapse text-left">
          <thead>
            <tr className="border-y border-ink-900 bg-ink-50 text-xs">
              <th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Description</th><th className="px-2 py-1.5">HSN</th>
              <th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Rate</th>
              <th className="px-2 py-1.5 text-right">Taxable</th><th className="px-2 py-1.5 text-right">GST %</th><th className="px-2 py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {i.items.map((l: any, n: number) => (
              <tr key={l.id} className="border-b border-line align-top">
                <td className="px-2 py-1.5">{n + 1}</td>
                <td className="px-2 py-1.5"><p className="font-medium">{l.description}</p>{l.modelNumber && <p className="text-xs text-ink-500">{l.modelNumber}</p>}</td>
                <td className="px-2 py-1.5">{l.hsnCode ?? ''}</td>
                <td className="tabular px-2 py-1.5 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                <td className="tabular px-2 py-1.5 text-right">{inr2(l.unitPrice)}</td>
                <td className="tabular px-2 py-1.5 text-right">{inr2(l.taxable)}</td>
                <td className="tabular px-2 py-1.5 text-right">{qtyLabel(l.gstRate)}</td>
                <td className="tabular px-2 py-1.5 text-right">{inr2(l.taxable + l.gst)}</td>
              </tr>))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between gap-6">
          <div className="max-w-sm">
            <p className="text-xs font-semibold uppercase text-ink-500">Amount in words</p>
            <p className="font-medium">{rupeesInWords(Number(i.grandTotal))}</p>
            <p className="mt-3 text-xs text-ink-500">Payment status: {INVOICE_STATUS.byValue[i.status as 'issued']?.label ?? i.status}</p>
            {i.note && <p className="mt-2 text-xs text-ink-600">{i.note}</p>}
          </div>
          <dl className="tabular w-64 space-y-1">
            <div className="flex justify-between"><dt>Taxable value</dt><dd>{inr2(i.taxableTotal)}</dd></div>
            {intra ? (
              <>
                <div className="flex justify-between"><dt>CGST</dt><dd>{inr2(gst / 2)}</dd></div>
                <div className="flex justify-between"><dt>SGST</dt><dd>{inr2(gst / 2)}</dd></div>
              </>
            ) : <div className="flex justify-between"><dt>IGST</dt><dd>{inr2(gst)}</dd></div>}
            <div className="flex justify-between border-t-2 border-ink-900 pt-1 text-base font-bold"><dt>Total</dt><dd>{inr2(i.grandTotal)}</dd></div>
            {Number(i.paidAmount) > 0 && <div className="flex justify-between"><dt>Received</dt><dd>{inr2(i.paidAmount)}</dd></div>}
            <div className="flex justify-between font-semibold"><dt>Balance due</dt><dd>{inr2(i.outstanding)}</dd></div>
          </dl>
        </div>

        <footer className="mt-12 flex justify-between text-xs text-ink-600">
          <p>This is a computer-generated invoice.</p>
          <p className="text-right">For {co.name}<br /><br /><br />Authorised signatory</p>
        </footer>
      </article>
    </div>
  );
}
